import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import {
  RequestState,
  useAsyncOperationState,
} from '../../../../../dorian-shared/hooks/useAsyncOperationState';
import { Promotion } from '../../types/PromotionTypes';
import {
  MissingSaleAnswer,
  SalesEventResponse,
  SelectedSaleAnswer,
} from './types/SalesTypes';
import { api } from '../../../../api';
import { showToast } from '../../../../ui/utils';
import { ConfirmModal } from '../../../../ui/ConfirmModal/ConfirmModal';
import { SalesForm } from './SalesForm';
import { useReportDirty } from '../../hooks/useReportDirty';
import {
  buildSalesSavePayload,
  formatMissingSaleAnswer,
  hydrateSelectedAnswers,
  selectionFingerprint,
} from './salesUtils';

interface SalesEditorProps {
  promotion: Promotion;
  isEditor: boolean;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function LoadingState(props: { requestState: RequestState }) {
  switch (props.requestState) {
    case RequestState.Loading:
      return (
        <div className="text-center py-3 mt-3">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    case RequestState.Error:
      return (
        <Alert variant="danger" className="mb-0 mt-3">
          Failed to load Sales configuration. Please try again later.
        </Alert>
      );
    default:
      return null;
  }
}

export function SalesEditor(props: SalesEditorProps) {
  const { promotion, isEditor, onSaved, onDirtyChange } = props;
  const promotionHasSchedule = Boolean(promotion.startTime && promotion.endTime);
  const promotionHasStory = Boolean(promotion.storyUuid?.trim());
  const canConfigure = promotionHasSchedule && promotionHasStory;

  const [saving, setSaving] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<SelectedSaleAnswer[]>([]);
  const [savedFingerprint, setSavedFingerprint] = useState(() => selectionFingerprint([]));
  const [missing, setMissing] = useState<MissingSaleAnswer[]>([]);

  const [requestState, {
    setToLoading,
    setToError,
    setToSuccess,
  }] = useAsyncOperationState();

  const applySalesResponse = useCallback((response: Pick<SalesEventResponse, 'answers' | 'missing'>) => {
    const nextSelected = hydrateSelectedAnswers(response.answers || []);
    const nextMissing = response.missing || [];
    setSelectedAnswers(nextSelected);
    setMissing(nextMissing);
    setSavedFingerprint(selectionFingerprint(nextSelected, nextMissing));
  }, []);

  const loadSales = useCallback(() => {
    if (!promotionHasStory) {
      setSelectedAnswers([]);
      setMissing([]);
      setSavedFingerprint(selectionFingerprint([]));
      setToSuccess();
      return;
    }

    setToLoading();
    api.get<SalesEventResponse>(`/v1/tools/promotions/${promotion.id}/sales-event`)
      .then((response) => {
        applySalesResponse(response.data);
        setToSuccess();
      })
      .catch(() => setToError());
  }, [applySalesResponse, promotion.id, promotionHasStory, setToError, setToLoading, setToSuccess]);

  useEffect(() => {
    loadSales();
  }, [loadSales, promotion.storyUuid]);

  const handleSave = () => {
    if (!promotionHasSchedule) {
      showToast({
        textMessage: 'Set promotion start and end times before saving Sales configuration',
        variant: 'danger',
      });
      return;
    }

    if (!promotion.storyUuid?.trim()) {
      showToast({
        textMessage: 'Set a story UUID on this promotion before saving Sales configuration',
        variant: 'danger',
      });
      return;
    }

    setSaving(true);
    api.put<SalesEventResponse>(
      `/v1/tools/promotions/${promotion.id}/sales-event`,
      buildSalesSavePayload(selectedAnswers),
    )
      .then((response) => {
        applySalesResponse(response.data);
        showToast({ textMessage: 'Sale saved', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to save Sales configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  const handleRemove = () => {
    setSaving(true);
    api.delete(`/v1/tools/promotions/${promotion.id}/sales-event`)
      .then(() => {
        applySalesResponse({ answers: [], missing: [] });
        setRemoveConfirmOpen(false);
        showToast({ textMessage: 'Sale removed', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to remove Sales configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  const dirty = requestState === RequestState.Success
    && selectionFingerprint(selectedAnswers, missing) !== savedFingerprint;
  useReportDirty(dirty, onDirtyChange);

  if (requestState !== RequestState.Success) {
    return <LoadingState requestState={requestState} />;
  }

  const hasConfiguredSale = savedFingerprint !== selectionFingerprint([]);

  return (
    <div className="mt-3">
      <ConfirmModal
        show={removeConfirmOpen}
        onHide={() => setRemoveConfirmOpen(false)}
        title="Remove Sale?"
        confirmButtonTitle="Remove"
        onConfirm={handleRemove}
        onCancel={() => setRemoveConfirmOpen(false)}
        isLoading={saving}
      >
        This will delete the sale configuration for this promotion. This cannot be undone.
      </ConfirmModal>

      {hasConfiguredSale && isEditor ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0 text-dark font-weight-bold">Sale</h5>
          <Button
            type="button"
            variant={null as any}
            className="text-white border-0"
            style={{ backgroundColor: '#888888' }}
            onClick={() => setRemoveConfirmOpen(true)}
            disabled={saving}
          >
            Remove Sale
          </Button>
        </div>
      ) : (
        <h5 className="mb-3 text-dark font-weight-bold">Sale</h5>
      )}

      {!promotionHasSchedule && isEditor && (
        <Alert variant="warning">
          Set the promotion start and end times above before configuring Sales.
        </Alert>
      )}

      {promotionHasSchedule && !promotionHasStory && isEditor && (
        <Alert variant="warning">
          Set a story UUID on this promotion before selecting sale answers.
        </Alert>
      )}

      {missing.length > 0 && (
        <Alert variant="warning">
          <div className="font-weight-bold mb-1">
            Some saved sale answers are no longer in this story.
          </div>
          <div className="mb-1">
            They will be dropped the next time you save. Re-select them if they still belong on this sale.
          </div>
          <ul className="mb-0 pl-3">
            {missing.map((row) => (
              <li key={`${row.episodeUuid}|${row.nodeTitle}|${row.answerIndex}`}>
                {formatMissingSaleAnswer(row)}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <SalesForm
        promotionId={promotion.id}
        selectedAnswers={selectedAnswers}
        onSelectedAnswersChange={setSelectedAnswers}
        disabled={!isEditor || !canConfigure}
        saving={saving}
        dirty={dirty}
        onSave={handleSave}
      />
    </div>
  );
}
